import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import CustomModal from "../../../packages/CustomModal";
import IconButton from "../../../packages/IconButton";
import { BackIcon } from "../../../packages/SVGs";

import { CHEMICAL_FIELDS } from "../chemicalLibrary.constants";
import {
  ChemicalLibDropdown,
  ChemicalLibInputBox,
} from "./ChemicalLibFormControls";
import {
  CATEGORY_FILTER_MAP,
  REQUIRED_ADD_CHEMICAL_FIELDS,
} from "./ChemicalLibraryAddEditDialog.constants";
import {
  applySelectedChemicalFormState,
  buildChemicalOptionsByCategory,
  calculateDensityFromConcentration,
  formatChemicalDataToFormData,
  formatChemicalDisplayName,
  getSelectedChemicalFromControls,
} from "./ChemicalLibraryAddEditDialog.util";

import {
  CustomModalButtons,
  FEATURE_SCOPE,
} from "../../../constants/global.constants";

import "./ChemicalLibraryAddEditDialog.scss";

const ChemicalLibHeader = ({ onClose, isAddMode }) => {
  const { t } = useTranslation();
  return (
    <div>
      <IconButton tooltip={t("ValueStrings.back")} onClick={onClose}>
        <BackIcon />
      </IconButton>
      {isAddMode
        ? t("chemicalLibrary.addChemicalDlgHeading")
        : t("chemicalLibrary.editChemicalDlgHeading")}
    </div>
  );
};

const ChemicalLibraryAddEditDialog = ({
  onClose,
  chemicalRowData,
  chemicalLibrary = [],
  mode = "edit",
  scope = FEATURE_SCOPE.USER,
  isLoading,
  onSubmit,
  shouldResetData,
}) => {
  const { t } = useTranslation();
  const isAddMode = mode === "add";
  const isUserScope = scope === FEATURE_SCOPE.USER;
  const isCustomChemicalEditable =
    !isAddMode && isUserScope && Boolean(chemicalRowData?.is_custom);

  const chemicalFormData = useMemo(() => {
    return formatChemicalDataToFormData(
      t,
      chemicalRowData,
      isAddMode,
      isCustomChemicalEditable,
    );
  }, [chemicalRowData, isAddMode, isCustomChemicalEditable, t]);

  const [invalidFields, setInvalidFields] = useState([]);
  const [formControls, setFormControls] = useState(chemicalFormData);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const chemicalOptionsByCategory = useMemo(() => {
    return buildChemicalOptionsByCategory(chemicalLibrary);
  }, [chemicalLibrary]);

  const getCategoryChemicalOptions = (categoryValue) => {
    const mappedCategory = CATEGORY_FILTER_MAP[categoryValue];
    return chemicalOptionsByCategory[mappedCategory] || [];
  };

  const handleModalClose = ({ target: { id } }) => {
    if (id === CustomModalButtons.CONFIRM) {
      if (isAddMode) {
        const symbol = formControls[CHEMICAL_FIELDS.symbol].value;
        const concentration =
          formControls[CHEMICAL_FIELDS.bulkConcentration].value;

        const updatedChemicalData = {
          chemical: formControls[CHEMICAL_FIELDS.chemical].value,
          price: Number(formControls[CHEMICAL_FIELDS.price].value),
          unit: formControls[CHEMICAL_FIELDS.unit].value,
          currency: "USD",
          category:
            CATEGORY_FILTER_MAP[formControls[CHEMICAL_FIELDS.category].value],
          symbol,
          display_name: formatChemicalDisplayName(symbol, concentration),
          bulk_density: Number(formControls[CHEMICAL_FIELDS.bulkDensity].value),
          bulk_concentration: Number(concentration),
          // Refer to ChemicalVisibilityToggleRenderer for details
          // inherit_to_plants: true,
        };

        onSubmit(updatedChemicalData, isFormInvalid);
      } else {
        const concentration =
          formControls[CHEMICAL_FIELDS.bulkConcentration].value;

        const updatedChemicalData = {
          ...chemicalRowData,
          price: formControls.price.value,
          unit: formControls.unit.value,
        };

        if (isCustomChemicalEditable) {
          updatedChemicalData.bulk_concentration = Number(concentration);
          updatedChemicalData.display_name = formatChemicalDisplayName(
            chemicalRowData?.symbol,
            concentration,
          );
        }

        onSubmit(updatedChemicalData, isFormInvalid);
      }
    }
  };

  const handleControlChange = (event) => {
    const { id, data, hasError } = event;
    let newControls = { ...formControls };

    const controlValue = data?.value ?? data?.name ?? data?.id ?? "";

    newControls = {
      ...newControls,
      [id]: { ...newControls[id], value: controlValue },
    };

    if (hasError !== undefined) {
      setInvalidFields((prev) =>
        hasError
          ? [...new Set([...prev, id])]
          : prev.filter((fieldId) => fieldId !== id),
      );
    }

    if (id === CHEMICAL_FIELDS.category && isAddMode) {
      const categoryOptions = getCategoryChemicalOptions(controlValue);
      applySelectedChemicalFormState(
        newControls,
        categoryOptions[0],
        categoryOptions,
      );
    }

    if (id === CHEMICAL_FIELDS.chemical && isAddMode) {
      const selectedChemical = (
        newControls[CHEMICAL_FIELDS.chemical]?.options || []
      ).find((option) => option.value === controlValue);

      applySelectedChemicalFormState(newControls, selectedChemical);
    }

    if (id === CHEMICAL_FIELDS.bulkConcentration) {
      const symbol = newControls[CHEMICAL_FIELDS.symbol].value;
      const concentration = controlValue;

      newControls[CHEMICAL_FIELDS.displayName] = {
        ...newControls[CHEMICAL_FIELDS.displayName],
        value: formatChemicalDisplayName(symbol, concentration),
      };

      if (isAddMode || isCustomChemicalEditable) {
        const selectedChemical = isAddMode
          ? getSelectedChemicalFromControls(newControls)
          : chemicalRowData;

        const density = calculateDensityFromConcentration(
          selectedChemical,
          concentration,
        );

        newControls[CHEMICAL_FIELDS.bulkDensity] = {
          ...newControls[CHEMICAL_FIELDS.bulkDensity],
          value:
            density !== undefined && density !== null && density !== ""
              ? `${density}`
              : "0.00",
          disabled: true,
        };
      }
    }

    const hasChanged = !isNaN(controlValue)
      ? parseFloat(controlValue) !== parseFloat(formControls[id].value)
      : controlValue !== formControls[id].value;

    if (hasChanged) {
      setHasUnsavedChanges(true);
      setFormControls(newControls);
    }
  };

  const isFormInvalid = useMemo(
    () => invalidFields.length > 0,
    [invalidFields],
  );

  const hasMissingRequiredFields = useMemo(() => {
    if (!isAddMode) {
      return false;
    }

    return REQUIRED_ADD_CHEMICAL_FIELDS.some((fieldId) => {
      const value = formControls[fieldId]?.value;
      return value === "" || value === null || value === undefined;
    });
  }, [formControls, isAddMode]);

  const isConfirmDisabled = useMemo(
    () => isFormInvalid || hasMissingRequiredFields || !hasUnsavedChanges,
    [isFormInvalid, hasMissingRequiredFields, hasUnsavedChanges],
  );

  useEffect(() => {
    if (shouldResetData) {
      setFormControls(chemicalFormData);
      setHasUnsavedChanges(false);
    }
  }, [shouldResetData, chemicalFormData]);

  return (
    <>
      <CustomModal
        header={<ChemicalLibHeader onClose={onClose} isAddMode={isAddMode} />}
        confirmBtn={t("chemicalLibrary.saveChemical")}
        onModalClose={handleModalClose}
        width="850px"
        confirmBtnDisabled={isConfirmDisabled}
        hideCloseIcon
        isLoading={isLoading}
        loadingText={
          isAddMode
            ? t("chemicalLibrary.savingChemical")
            : t("chemicalLibrary.updatingChemical")
        }
      >
        <div className="add-edit-chemical-form--container">
          {Object.values(formControls).map((control) => (
            <div className="column bottom-margin-0" key={control.id}>
              {control.type === "dropdown" ? (
                <ChemicalLibDropdown
                  control={control}
                  onControlChange={handleControlChange}
                />
              ) : (
                <ChemicalLibInputBox
                  control={control}
                  onControlChange={handleControlChange}
                />
              )}
            </div>
          ))}
        </div>
      </CustomModal>
    </>
  );
};

export default ChemicalLibraryAddEditDialog;
